import AddressesContent, {
  BillingAddress,
  DeliveryAddress,
  MondialRelayLocation,
} from "@Types/addresses";
import CheerzApiAddresses, {
  CheerzApiBillingAddress,
  CheerzApiShippingAddress,
  KonnektorApiMondialRelayLocation,
} from "@Types/api/addresses";
import { ShippingMethodName } from "@Types/shipping";
import {
  convertShippingOptionFromApiToContent,
  convertShippingOptionFromContentToApi,
} from "@Utils/cart/shipping";

export function convertAddressesFromApiToContent(
  jsonContent: CheerzApiAddresses
): AddressesContent {
  const getBillingAddressValue = (field: keyof CheerzApiBillingAddress): string | undefined => {
    const billAddressValue = jsonContent.bill_address?.[field];
    return billAddressValue ? billAddressValue : undefined;
  };

  const getDeliveryAddressValue = (
    field: Exclude<keyof CheerzApiShippingAddress, "shipping_options">
  ): string | undefined => {
    const shipAddressValue = jsonContent.ship_address?.[field];
    return shipAddressValue ? shipAddressValue : undefined;
  };

  const billingAddress: BillingAddress = {
    city: getBillingAddressValue("city"),
    company: getBillingAddressValue("company"),
    country: getBillingAddressValue("country_name"),
    countryTag: getBillingAddressValue("country_tag"),
    details: getBillingAddressValue("address2"),
    firstName: getBillingAddressValue("first_name"),
    lastName: getBillingAddressValue("last_name"),
    postcode: getBillingAddressValue("zipcode"),
    state: getBillingAddressValue("state"),
    street: getBillingAddressValue("address1"),
    vatNumber: getBillingAddressValue("vat_number"),
  };

  const deliveryAddress: DeliveryAddress = {
    city: getDeliveryAddressValue("city"),
    company: getDeliveryAddressValue("company"),
    country: getDeliveryAddressValue("country_name"),
    countryTag: getDeliveryAddressValue("country_tag"),
    details: getDeliveryAddressValue("address2"),
    email: getDeliveryAddressValue("mail"),
    firstName: getDeliveryAddressValue("first_name"),
    lastName: getDeliveryAddressValue("last_name"),
    mondialRelayNumber: getDeliveryAddressValue("mondial_relay_num"),
    phone: getDeliveryAddressValue("phone"),
    postcode: getDeliveryAddressValue("zipcode"),
    shippingOptions: {
      express: undefined,
      mondialRelay: undefined,
      normal: undefined,
    },
    state: getDeliveryAddressValue("state"),
    street: getDeliveryAddressValue("address1"),
    tag: getDeliveryAddressValue("tag"),
    userCountryTag: getDeliveryAddressValue("user_country_tag"),
  };
  if (jsonContent.ship_address) {
    if (jsonContent.ship_address.shipping_options.express) {
      deliveryAddress.shippingOptions.express = convertShippingOptionFromApiToContent(
        jsonContent.ship_address.shipping_options.express
      );
    }

    if (jsonContent.ship_address.shipping_options.mondial_relay) {
      deliveryAddress.shippingOptions.mondialRelay = {
        express: convertShippingOptionFromApiToContent(
          jsonContent.ship_address.shipping_options.mondial_relay.express
        ),
        normal: convertShippingOptionFromApiToContent(
          jsonContent.ship_address.shipping_options.mondial_relay.normal
        ),
      };
    }

    if (jsonContent.ship_address.shipping_options.normal) {
      deliveryAddress.shippingOptions.normal = convertShippingOptionFromApiToContent(
        jsonContent.ship_address.shipping_options.normal
      );
    }
  }

  const temporaryEmail = jsonContent.user.temporary_email;

  return {
    billing: billingAddress,
    delivery: deliveryAddress,
    user: {
      temporaryEmail: temporaryEmail ? temporaryEmail : undefined,
    },
  };
}

export function convertAddressesFromContentToApi(
  billing: Partial<BillingAddress> | null,
  delivery: Partial<DeliveryAddress>
): CheerzApiAddresses {
  let billingAddress: CheerzApiBillingAddress | null = null;

  if (billing) {
    billingAddress = {
      address1: billing.street || null,
      address2: billing.details || null,
      city: billing.city || null,
      company: billing.company || null,
      country_name: billing.country || null,
      country_tag: billing.countryTag || "",
      first_name: billing.firstName || null,
      last_name: billing.lastName || null,
      state: billing.state || null,
      vat_number: billing.vatNumber || null,
      zipcode: billing.postcode || null,
    };
  }

  const deliveryAddress: CheerzApiShippingAddress = {
    address1: delivery.street || null,
    address2: delivery.details || null,
    city: delivery.city || null,
    company: delivery.company || null,
    country_name: delivery.country || null,
    country_tag: delivery.countryTag || "",
    first_name: delivery.firstName || null,
    last_name: delivery.lastName || null,
    mail: delivery.email || null,
    mondial_relay_num: delivery.mondialRelayNumber || null,
    phone: delivery.phone || null,
    shipping_options: {
      express: null,
      mondial_relay: null,
      normal: null,
    },
    state: delivery.state || null,
    tag: delivery.tag || null,
    user_country_tag: delivery.userCountryTag || null,
    zipcode: delivery.postcode || null,
  };

  if (delivery.shippingOptions && delivery.shippingOptions.express) {
    deliveryAddress.shipping_options.express = convertShippingOptionFromContentToApi(
      delivery.shippingOptions.express
    );
  }

  if (delivery.shippingOptions && delivery.shippingOptions.mondialRelay) {
    deliveryAddress.shipping_options.mondial_relay = {
      express: convertShippingOptionFromContentToApi(delivery.shippingOptions.mondialRelay.express),
      normal: convertShippingOptionFromContentToApi(delivery.shippingOptions.mondialRelay.normal),
    };
  }

  if (delivery.shippingOptions && delivery.shippingOptions.normal) {
    deliveryAddress.shipping_options.normal = convertShippingOptionFromContentToApi(
      delivery.shippingOptions.normal
    );
  }

  return {
    bill_address: billingAddress,
    ship_address: deliveryAddress,
    user: {
      temporary_email: delivery.email || null,
    },
  };
}

export const convertMondialRelayLocationsFromApiToContent = (
  locationsContent: KonnektorApiMondialRelayLocation[]
): MondialRelayLocation[] => {
  return locationsContent.map((location) => {
    return {
      city: location.city,
      company: location.company,
      countryTag2: location.country_tag,
      details: location.address2 ? location.address2 : undefined,
      distance: location.distance,
      mondialRelayNumber: location.mondial_relay_num,
      openingHours: location.opening_hours,
      postcode: location.zipcode,
      street: location.address1,
    };
  });
};

export const formatStreet = (
  streetNumber: string | undefined,
  street: string | undefined
): string =>
  !!streetNumber && !!street ? `${streetNumber} ${street}` : !!street ? `${street}` : "";

export const isPickupSelected = (shippingMethod: ShippingMethodName | undefined): boolean =>
  shippingMethod === "mondialRelay";

export const getCountryFlagImageSrc = (countryTag: string): string =>
  `https://flagcdn.com/h20/${countryTag}.png`;
